import {
  ApplicationRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output, ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {BaseComponent} from '@core/base.component';
import {PermissionEnum} from '@core/consts/permissions';
import {Folder} from "@core/model/folder";
import {SecurityFacade} from '@core/ngrx/facades/security.facade';
import {ConfirmationService, MenuItem, MessageService, PrimeIcons, TreeDragDropService, TreeNode} from "primeng/api";
import {UserMap} from "@core/model/user-map";
import {MapConfigStateService} from "@core/service/map-config-state.service";
import {Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {mapConfigActions, datacenterActions} from "@core/ngrx/actions";
import {AppStateService} from "@core/service/app-state.service";
import {Subject, take, takeUntil, tap} from "rxjs";
import {Actions, ofType} from "@ngrx/effects";
import _ from "lodash";
import {MAP_STATUS_FAILED, MAP_STATUS_IN_PROGRESS} from "@core/consts/map.constants";
import {MapService} from "@core/service/map.service";
import {environment} from "@environment/environment";
import {UrlFormatterService} from "@core/service/url-formatter.service";
import {Clipboard} from "@angular/cdk/clipboard";
import {RowError} from '@core/model/row-error';
import {TreeNodeDropEvent} from "primeng/tree";

@Component({
  selector: 'app-folder-config',
  templateUrl: './folder-config.component.html',
  styleUrl: './folder-config.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  providers: [TreeDragDropService, ConfirmationService]
})
export class FolderConfigComponent extends BaseComponent implements OnInit, OnDestroy {
  _folders: TreeNode[] = [];
  _filteredFolders: TreeNode[] = [];
  contextItems!: MenuItem[]
  rowErrors: RowError[] = [];
  contextSelectedNode: TreeNode | null = null;
  isEditVisible: boolean = false;
  isNewFolder: boolean = false;
  isErrorsVisible: boolean = false;
  folderName: string = '';
  confirmationService: ConfirmationService = inject(ConfirmationService);
  mapService: MapService = inject(MapService);
  messageService: MessageService = inject(MessageService);
  mapConfigState: MapConfigStateService = inject(MapConfigStateService);
  appStateService: AppStateService = inject(AppStateService);
  urlFormatter: UrlFormatterService = inject(UrlFormatterService);
  clipboard: Clipboard = inject(Clipboard);
  router = inject(Router);
  private securityFacade = inject(SecurityFacade);
  readonly store: Store = inject(Store);
  actions$: Actions = inject(Actions);
  destroy$: Subject<boolean> = new Subject<boolean>();
  _hasChangesToSave: boolean = false;

  @Output()
  hasChangesToSave = new EventEmitter<boolean>();

  @Output()
  mapSelected = new EventEmitter<UserMap>();

  @Output()
  mapSelectedToModify = new EventEmitter<TreeNode>();

  @Output()
  selectionCancelled = new EventEmitter<boolean>();

  @Output()
  folderSelected = new EventEmitter<TreeNode>();

  @Input()
  showMaps: boolean = true;

  @Input()
  canModify: boolean = true;

  @Input()
  selectedNode: TreeNode | null = null;


  private setSelectedNode(nodes: TreeNode[] | null) {
    for (const folder of nodes) {
      if (folder.data._id === this.selectedNode.data._id) {
        this.selectedNode = folder;
        return;
      }
      if (folder.children && folder.children.length > 0) {
        this.setSelectedNode(folder.children);
      }
    }
  }

  ngOnInit(): void {
    this.subscribeToDeleteMapSuccess();
    this.subscribeToLoadFolders();
    this.subscribeToSaveFoldersSuccess();
    this.sendLoadFoldersAction();
    this.subscribeToLoadDatacentersWithErrorsSuccess();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  saveFolderConfig() {
    if (this._hasChangesToSave) {
      const foldersToSave: Folder[] = [];
      for (let node of this._folders) {
        foldersToSave.push(this.extractFolderFromNode(node));
      }
      this.store.dispatch(mapConfigActions.saveFolders({folders: foldersToSave}));
      this._hasChangesToSave = false;
    }
  }

  saveFolderChanges() {
    this.isEditVisible = false;
    if (this.isNewFolder) {
      const folder: Folder = {
        _id: '',
        name: this.folderName,
        folders: [],
        maps: []
      }
      const node: TreeNode = {
        label: this.folderName,
        collapsedIcon: PrimeIcons.FOLDER,
        expandedIcon: PrimeIcons.FOLDER_OPEN,
        data: folder,
        children: [],
        expanded: false
      };
      if (!this.contextSelectedNode) {
        this._folders.push(node);
      } else {
        this.contextSelectedNode.children.push(node);
      }

    } else {
      this.contextSelectedNode.label = this.folderName;
    }
    this.folderName = '';
    this.contextSelectedNode = null;
    this.hasChangesToSave.emit(true)
    this._hasChangesToSave = true;
    this._folders = _.cloneDeep(this._folders);
  }

  nodeSelected(node: TreeNode) {
    this.contextSelectedNode = node;
    this.selectedNode = node;
    let items: MenuItem[] = [];
    if (node.data.hasOwnProperty("folders")) {
      items = [
        {label: 'New Folder', icon: 'pi pi-plus', command: (event) => this.addNewFolder()},
        {label: 'Change Name', icon: 'pi pi-file-edit', command: (event) => this.editFolder()},
        {label: 'Delete', icon: 'pi pi-times', command: (event) => this.deleteFolder()},
      ]
    } else {
      if (!node.data.errorMsg) {
        items = [
          {
            label: 'View',
            icon: 'pi pi-images',
            command: () => this.mapSelected.emit(this.selectedNode.data),
            permission: PermissionEnum.MAPS_OPEN
          },
          {
            label: 'Edit',
            icon: 'pi pi-file-edit',
            command: () => this.mapSelectedToModify.emit(this.selectedNode),
            permission: PermissionEnum.MAPS_EDIT
          },
          {
            label: 'Copy Map Link',
            icon: 'pi pi-link',
            command: () => this.copyMapLink(this.selectedNode),
            permission: PermissionEnum.MAPS_COPY_LINK
          },
        ];
      } else {
        //TODO review this the endpoint

        items.push({
          label: 'View Errors',
          icon: 'pi pi-exclamation-triangle',
          command: () => {
            this.isErrorsVisible = true;
            this.rowErrors = [];
            this.store.dispatch(datacenterActions.getDatacentersWithErrors({mapId: this.selectedNode.data._id}));
          },
          permission: PermissionEnum.AUDIT_LOGS_VIEW_LIST
        });
      }
      items.push({
        label: 'Delete',
        icon: 'pi pi-times',
        command: () => this.deleteMap(this.selectedNode),
        permission: PermissionEnum.MAPS_DELETE
      })
    }

    this.securityFacade.userAccess$
      .pipe(take(1))
      .subscribe(({permissions}) => {
        // @ts-ignore
        this.contextItems = items.filter(item => !item.permission || permissions.includes(item.permission));
      });
  }

  onNodeDrop(event: TreeNodeDropEvent) {
    this.hasChangesToSave.emit(true);
    this._hasChangesToSave = true;
  }

  getNodeByKey(nodes: TreeNode[], targetKey: string): TreeNode {
    let matchedNode: TreeNode;
    for (let node of nodes) {
      let matched = node.key.toLowerCase().includes(targetKey);
      if (matched) {
        return node;
      }
      if (node.children) {
        matchedNode = this.getNodeByKey(node.children, targetKey);
        if (matchedNode) {
          return matchedNode;
        }
      }
    }
    return null;
  }


  addRootFolder() {
    this.contextSelectedNode = null;
    this.folderName = '';
    this.isEditVisible = true;
    this.isNewFolder = true;
  }

  cancelSelection() {
    this.selectionCancelled.emit(true);
  }

  confirmSelection() {
    this.folderSelected.emit(this.selectedNode)
  }


  private extractFolderFromNode(node: TreeNode): Folder {
    const folder: Folder = _.cloneDeep(node.data as Folder);
    folder.name = node.label;
    for (let childNode of node.children || []) {
      if (childNode.data.hasOwnProperty("folders")) {
        folder.folders.push(this.extractFolderFromNode(childNode));
      } else {
        folder.maps.push({
          _id: childNode.data._id,
          name: childNode.data.name,
          userId: childNode.data.userId,
          status: childNode.data.status,
          errorMsg: childNode.data.errorMsg
        });
      }
    }
    return folder;
  }

  private createNodes(parent: TreeNode, folders: Folder[]): TreeNode[] {
    const nodes: TreeNode[] = [];
    for (let folder of folders) {

      const node: TreeNode = {
        label: folder.name,
        key: folder._id,
        collapsedIcon: PrimeIcons.FOLDER,
        expandedIcon: PrimeIcons.FOLDER_OPEN,
        data: folder,
        parent: parent,
        expanded: false,
        draggable: true,
        droppable: true,

      };
      node.children = this.createNodes(node, folder.folders),
        folder.folders = [];
      nodes.push(node);
      if (this.showMaps) {
        if (folder.maps && folder.maps.length > 0) {
          for (let map of folder.maps) {
            const mapNode: TreeNode = {
              label: map.name,
              key: map._id,
              icon: map.status == MAP_STATUS_IN_PROGRESS ? PrimeIcons.INFO_CIRCLE : PrimeIcons.MAP,
              data: map,
              styleClass: map.status == MAP_STATUS_FAILED ? 'error-text' : (map.status == MAP_STATUS_IN_PROGRESS ? 'info-text' : null),
              type: map.status == MAP_STATUS_FAILED ? 'error' : (map.status == MAP_STATUS_IN_PROGRESS ? 'inProgress' : null),
              draggable: true,
              droppable: false,
            };
            node.children!.push(mapNode);
          }
          folder.maps = [];
        }
      }
    }
    return nodes;
  }

  private addNewFolder() {
    this.folderName = '';
    this.isEditVisible = true;
    this.isNewFolder = true;
  }

  private editFolder() {
    this.folderName = '';
    this.isEditVisible = true;
    this.isNewFolder = false;
    this.folderName = this.contextSelectedNode.label
  }

  private deleteFolder() {
    const isFolderEmpty = this.isFolderEmpty(this.contextSelectedNode);
    if (isFolderEmpty) {
      this.hasChangesToSave.emit(true)
      this._hasChangesToSave = true;
      if (this.contextSelectedNode.parent) {
        const index = this.contextSelectedNode.parent.children.indexOf(this.contextSelectedNode);
        if (index > -1) {
          this.contextSelectedNode.parent.children.splice(index, 1);
        }
      } else {
        const noFilteredNode = this.getNodeByKey(this._folders, this.contextSelectedNode.key.toLowerCase());
        const index = this._folders.indexOf(noFilteredNode);
        if (index > -1) {
          this._folders.splice(index, 1)
          this._folders = _.cloneDeep(this._folders);
        }
      }
    } else {
      //show error
      this.confirmationService.confirm({
        message: 'You cannot delete a folder that contains maps or subfolders with maps. Please remove or move all maps before deleting this folder.',
        header: 'Error',
        acceptButtonProps: {
          label: 'Accept',
          severity: 'info',
        },
        rejectVisible: false
      });
    }
  }

  private isFolderEmpty(treeNode: TreeNode): boolean {
    const existingMaps = treeNode.children.filter(c => !c.data.hasOwnProperty("folders")).length;
    if (existingMaps > 0) {
      return false;
    } else {
      for (let node of treeNode.children) {
        const isEmpty = this.isFolderEmpty(node);
        if (!isEmpty) {
          return false;
        }
      }
      return true;
    }
  }

  private sendLoadFoldersAction() {
    this.store.dispatch(mapConfigActions.loadFolders());
  }

  private subscribeToLoadFolders() {
    this.actions$.pipe(
      ofType(mapConfigActions.loadFoldersSuccess.type),
      takeUntil(this.destroy$),
      tap((data: any) => {
        this._folders = this.createNodes(null, _.cloneDeep(data.folders));
        if (this.selectedNode) {
          this.setSelectedNode(this._folders);
          let parent = this.selectedNode.parent;
          while (parent) {
            parent.expanded = true;
            parent = parent.parent;
          }
        }
        this._filteredFolders = this._folders;
      })
    ).subscribe();
  }

  private subscribeToSaveFoldersSuccess() {
    this.actions$.pipe(
      ofType(mapConfigActions.saveFoldersSuccess.type),
      takeUntil(this.destroy$),
      tap(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Folders Saved',
          detail: 'Folder configuration has been successfully saved.',
          life: 3000,
          icon: 'pi pi-check'
        });
        this._hasChangesToSave = false;
        this.hasChangesToSave.emit(false);
        this.sendLoadFoldersAction();
      })
    ).subscribe();
  }

  private deleteMap(node: any) {
    this.confirmationService.confirm({
      message: `Are you sure that you want to delete <b>${node.data.name}</b> Map ?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.store.dispatch(mapConfigActions.deleteMap({mapId: node.data._id, folderId: node.parent.data._id}));
      }
    });
  }

  private subscribeToDeleteMapSuccess() {
    this.actions$.pipe(
      ofType(mapConfigActions.deleteMapSuccess.type),
      takeUntil(this.destroy$),
      tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Map Deleted',
            detail: 'The map has been successfully deleted.',
            life: 3000,
            icon: 'pi pi-check'
          });
          this.selectedNode.parent.children = this.selectedNode.parent.children.filter(c => c.data._id !== this.selectedNode.data._id);
        }
      )).subscribe();
  }

  private subscribeToLoadDatacentersWithErrorsSuccess() {
    this.actions$.pipe(
      ofType(datacenterActions.getDatacentersWithErrorsSuccess.type),
      takeUntil(this.destroy$),
      tap((datacentersAction: any) => {
          datacentersAction.datacenters.forEach(dc => {
            if (dc.rowErrors && dc.rowErrors.length > 0) {
              this.rowErrors.push(...dc.rowErrors);
            }
          });
        }
      )).subscribe();
  }

  private copyMapLink(node: TreeNode) {
    const encodedUserId = this.urlFormatter.formatUserIdForUrl(node.data.userId);
    const mapLink = `${environment.envUrl}/map-config/${encodedUserId}/${node.data._id}`;
    this.clipboard.copy(mapLink);

    this.messageService.add({
      severity: 'success',
      summary: 'Link copied',
      detail: 'Map link has been copied to clipboard',
      life: 3000,
      icon: 'pi pi-copy'
    });
  }
}
